import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import SEO from "../seo/SEO";

import dreamlandImage from "../assets/images/projects/dreamland.jpg";
import crmDashboardImage from "../assets/images/projects/coconut-ecommerce-admin.png";
import tryOnImage from "../assets/images/projects/tryon_mobile.png";
import pressMedImage from "../assets/images/projects/presmed_dash.png";
import jabalaImage from "../assets/images/projects/Jabala_admin.png"
import crmCocoNutImage from "../assets/images/projects/coconut-ecommerce.JPG"
import daabJallImage from "../assets/images/projects/DaabJall_MobileApp.png"

import {
  ArrowRight,
  ExternalLink,
  ShoppingCart,
  BrainCircuit,
  LayoutDashboard,
  Smartphone,
  CheckCircle2,
  Gem,
} from "lucide-react";

import "./CaseStudies.css";


/* =========================================================
   CASE STUDIES
========================================================= */

const caseStudies = [
  {
    id: 1,

    icon: <ShoppingCart size={30} />,

    title: "Dreamland Bites",

    category: "Ecommerce Platform",

    description:
      "Premium bakery ecommerce ecosystem with modern UI/UX, product catalogue management, responsive ordering workflows and scalable backend integration.",

    technologies: [
      "React.js",
      "FastAPI",
      "PostgreSQL",
      "Responsive UI",
    ],

    metrics: [
      "Mobile Optimized",
      "Modern UI/UX",
      "Ecommerce Workflow",
    ],

    image: dreamlandImage,

    liveLink: "https://dreamlandbitess.com/",

    hasLiveProject: true,
  
  },


  {
    id: 2,

    icon: <BrainCircuit size={30} />,

    title: "PressMed",

    category: "Healthcare Software Platform",

    description:
      "Digital healthcare and prescription management platform with intelligent document processing, patient workflows, reporting dashboards and AI-assisted healthcare operations.",

    technologies: [
      "React.js",
      "FastAPI",
      "AI / OCR",
      "PostgreSQL",
    ],

    metrics: [
      "Digital Workflow",
      "Document Processing",
      "Healthcare Dashboard",
    ],

    image: pressMedImage,

    liveLink: null,

    hasLiveProject: false,
  },


  {
    id: 3,

    icon: <LayoutDashboard size={30} />,

    title: "Ecommerce Admin Dashboard",

    category: "Business Automation",

    description:
      "Custom ecommerce administration and analytics dashboard for product management, order processing, operational reporting and business workflow automation.",

    technologies: [
      "React.js",
      "FastAPI",
      "PostgreSQL",
      "Analytics",
    ],

    metrics: [
      "Order Management",
      "Business Analytics",
      "Admin Workflow",
    ],

    image: crmDashboardImage,

    liveLink: null,

    hasLiveProject: false,
  },
{
  id: 4,

  icon: <LayoutDashboard size={30} />,

  title: "Coconut Multiproduct",

  category: "Ecommerce & Business Management Platform",

  description:
    "Custom ecommerce and business management platform for Coconut Multiproduct, integrating product management, online ordering, payment workflows, administration dashboards and operational reporting.",

  technologies: [
    "React.js",
    "FastAPI",
    "PostgreSQL",
    "Analytics",
  ],

  metrics: [
    "Ecommerce Management",
    "Order Processing",
    "Business Dashboard",
  ],

  image: crmCocoNutImage,

  liveLink: "https://coconutmultiproduct.com/",

  hasLiveProject: true,
},
{
  id: 5,

  icon: <LayoutDashboard size={30} />,

  title: "Jabala MIS",

  category: "NGO Management & MIS Platform",

  description:
    "Custom NGO management and MIS platform developed for Jabala, supporting beneficiary management, centre operations, activities, attendance, reporting and administrative workflows across multiple locations.",

  technologies: [
    "React.js",
    "FastAPI",
    "PostgreSQL",
    "REST API",
  ],

  metrics: [
    "MIS Dashboard",
    "Multi-Centre Management",
    "Activity & Reporting System",
  ],

  image: jabalaImage,

  liveLink: "https://jabala.in/",

  hasLiveProject: true,
},
  {
    id: 6,

    icon: <Gem size={30} />,

    title: "TryOn Jewellery Application",

    category: "AI / Interactive Application",

    description:
      "Interactive jewellery TryOn application designed to help customers visualize jewellery products digitally before purchase, creating a more engaging ecommerce shopping experience.",

    technologies: [
      "React",
      "Image Processing",
      "Interactive UI",
      "Responsive Design",
    ],

    metrics: [
      "Virtual TryOn",
      "Interactive Experience",
      "Jewellery Ecommerce",
    ],

    image: tryOnImage,

    liveLink: null,

    hasLiveProject: false,
  },
  {
  id: 7,

  icon: <Smartphone size={30} />,

  title: "Daab Jall Mobile App",

  category: "Android Ecommerce Application",

  description:
    "Android ecommerce application developed for Daab Jall, enabling customers to browse coconut products, manage their cart, place online orders, maintain account information and access a streamlined mobile shopping experience.",

  technologies: [
    "React Native",
    "REST API",
    "Ecommerce",
    "Android",
  ],

  metrics: [
    "Published on Google Play",
    "Mobile Ecommerce",
    "Customer Ordering Workflow",
  ],

  image: daabJallImage,

  liveLink:
    "https://play.google.com/store/apps/details?id=com.coconutapp&pcampaignid=web_share",

  hasLiveProject: true,
},
];


/* =========================================================
   CASE STUDIES PAGE
========================================================= */

export default function CaseStudies() {

  const navigate = useNavigate();


  /* =======================================================
     NAVIGATION
  ======================================================= */

  const handleStartProject = () => {

    navigate("/contact?service=project");

  };


  const handleDiscussProject = (projectName) => {

    navigate(
      `/contact?project=${encodeURIComponent(projectName)}`
    );

  };


  /* =======================================================
     STRUCTURED DATA
  ======================================================= */

  const caseStudiesSchema = {

    "@context": "https://schema.org",

    "@type": "CollectionPage",

    name: "Software Development Case Studies",

    url:
      "https://www.bairagi-consultants.com/case-studies",

    description:
      "Software development case studies from Bairagi Consultants covering ecommerce platforms, healthcare software, dashboards, AI applications and interactive digital solutions.",

    publisher: {

      "@type": "Organization",

      name: "Bairagi Consultants",

      url:
        "https://www.bairagi-consultants.com",

    },

  };


  return (

    <>

      {/* =====================================================
          SEO
      ====================================================== */}

      <SEO
        title="Software Development Case Studies | Bairagi Consultants"
        description="Explore real software development projects including ecommerce platforms, healthcare software, business dashboards, AI solutions and interactive jewellery TryOn applications."
        path="/case-studies"
        schema={caseStudiesSchema}
      />


      <div className="case-studies-page">


        {/* =================================================
            HERO
        ================================================= */}

        <section className="case-hero">

          <div className="container">

            <motion.div
              className="case-hero-content"
              initial={{
                opacity: 0,
                y: 30,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.8,
              }}
            >

              <p className="case-label">

                SOFTWARE DEVELOPMENT PORTFOLIO

              </p>


              <h1>

                Real Software Systems

                <br />

                <span>

                  Built For Businesses

                </span>

              </h1>


              <p className="case-subtitle">

                Explore our software development
                projects covering ecommerce platforms,
                healthcare systems, business dashboards,
                AI applications and interactive
                digital experiences.

              </p>

            </motion.div>

          </div>

        </section>


        {/* =================================================
            CASE STUDIES GRID
        ================================================= */}

        <section className="case-grid-section">

          <div className="container">

            <div className="case-grid">

              {caseStudies.map((study, index) => (

                <motion.article
                  key={study.id}
                  className="case-card"
                  initial={{
                    opacity: 0,
                    y: 30,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.1,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.15,
                  }}
                >


                  {/* =========================================
                      IMAGE
                  ========================================== */}

                  <div className="case-image-wrapper">

                  {/* Decorative blurred background */}
                  <img
                    src={study.image}
                    alt=""
                    aria-hidden="true"
                    className="case-image-background"
                  />

                  {/* Actual application screenshot */}
                  <div className="case-image-frame">

                    <img
                      src={study.image}
                      alt={`${study.title} software project by Bairagi Consultants`}
                      className="case-image"
                      loading="lazy"
                    />

                  </div>

                </div>

                  {/* =========================================
                      CONTENT
                  ========================================== */}

                  <div className="case-content">

                    <div
                      className="case-icon"
                      aria-hidden="true"
                    >

                      {study.icon}

                    </div>


                    <span className="case-category">

                      {study.category}

                    </span>


                    <h2>

                      {study.title}

                    </h2>


                    <p>

                      {study.description}

                    </p>


                    {/* =======================================
                        TECH STACK
                    ======================================== */}

                    <div className="case-tech-stack">

                      {study.technologies.map((tech) => (

                        <span key={tech}>

                          {tech}

                        </span>

                      ))}

                    </div>


                    {/* =======================================
                        FEATURES
                    ======================================== */}

                    <div className="case-metrics">

                      {study.metrics.map((metric) => (

                        <div
                          className="case-metric"
                          key={metric}
                        >

                          <CheckCircle2 size={16} />


                          <span>

                            {metric}

                          </span>

                        </div>

                      ))}

                    </div>


                    {/* =======================================
                        ACTIONS
                    ======================================== */}

                    <div className="case-actions">

                      <button
                        type="button"
                        className="case-primary-btn"
                        onClick={() =>
                          handleDiscussProject(
                            study.title
                          )
                        }
                      >

                        Discuss Similar Project

                        <ArrowRight size={18} />

                      </button>


                      {study.hasLiveProject &&
                        study.liveLink && (

                          <a
                            href={study.liveLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="case-secondary-btn"
                            aria-label={`Open ${study.title} live project`}
                          >

                            Live Preview

                            <ExternalLink size={16} />

                          </a>

                        )}

                    </div>

                  </div>

                </motion.article>

              ))}

            </div>

          </div>

        </section>


        {/* =================================================
            SERVICES / INTERNAL LINKS
        ================================================= */}

        <section className="case-grid-section">

          <div className="container">

            <div className="case-final-card">

              <h2>

                Explore Our Software Development Services

              </h2>


              <p>

                Need a similar platform?
                Explore our ecommerce, AI,
                CRM, mobile application and
                dashboard development services.

              </p>


              <div className="case-actions">

                <Link
                  to="/ecommerce-development"
                  className="case-secondary-btn"
                >

                  Ecommerce Development

                </Link>


                <Link
                  to="/mobile-app-development"
                  className="case-secondary-btn"
                >

                  Mobile App Development

                </Link>


                <Link
                  to="/crm-software-development"
                  className="case-secondary-btn"
                >

                  CRM Development

                </Link>


                <Link
                  to="/ai-software-solutions"
                  className="case-secondary-btn"
                >

                  AI Solutions

                </Link>


                <Link
                  to="/custom-dashboard-development"
                  className="case-secondary-btn"
                >

                  Dashboard Development

                </Link>

              </div>

            </div>

          </div>

        </section>


        {/* =================================================
            FINAL CTA
        ================================================= */}

        <section className="case-final-section">

          <div className="container">

            <div className="case-final-card">

              <h2>

                Ready To Build Your Platform?

              </h2>


              <p>

                Let's create scalable software systems,
                AI-powered applications and enterprise
                digital platforms tailored to your
                business requirements.

              </p>


              <button
                type="button"
                className="case-primary-btn"
                onClick={handleStartProject}
              >

                Start Your Project

                <ArrowRight size={18} />

              </button>

            </div>

          </div>

        </section>


      </div>

    </>

  );

}